import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LocalConfig } from 'utils';

import { Slider } from '@atlas/components';
import { Bus, SiteConfigManager, useCasinoLastWinners } from '@atlas/core';
import { LastWinnerSkeleton } from '@atlas/ui';

import LastWinnersItem from './LastWinnersItem';

export const SLIDER_CONFIG = {
  autoplayDelay: 1500,
  buttonTimeout: 7000,
  slidesPerView: 1,
  slidesToRender: 3, // slidesPerView + 2
};

// this function is used to cut the queue of winners
// if the queue is bigger than queueLimit
// it will insert the new winners to the end of the queue
// and remove the outdated winners from the beginning of the queue
const QUEUE_LIMIT = 50;
const cutQueue = (prev, next) => {
  const mergeResult = [...prev, ...next];
  const overwhelmed = mergeResult.length > QUEUE_LIMIT;
  return mergeResult.slice(overwhelmed ? mergeResult.length - QUEUE_LIMIT : 0);
};

const LastWinners = ({ isAuthenticated, status = 'active' }) => {
  const isActive = SiteConfigManager.getConfig('config.config.lastWinners.mobile.isActive');
  const projectId = LocalConfig.get('projectId');

  const [swiper, setSwiper] = useState(null);
  const [autoplayActive, setAutoplayActive] = useState(true);
  const [winnersToShow, setWinnersToShow] = useState([]);
  const [queue, setQueue] = useState([]);
  const isInsertingSlides = useRef(false);

  const { lastWinners, loading } = useCasinoLastWinners(projectId);

  const autoplayStart = useCallback(() => {
    swiper?.autoplay?.start();
    setAutoplayActive(true);
  }, [swiper]);

  const autoplayStop = useCallback(() => {
    swiper?.autoplay?.stop();
    setAutoplayActive(false);
  }, [swiper]);

  const onSlideChangeTransitionEnd = useCallback(
    swiper => {
      const { slidesToRender } = SLIDER_CONFIG;
      const startIndex = 0;
      const endIndex = winnersToShow.length - SLIDER_CONFIG.slidesPerView;

      const needToInsert =
        !isInsertingSlides.current &&
        queue.length > 0 &&
        swiper.realIndex % winnersToShow.length === startIndex;

      if (needToInsert) {
        const nextWinnersToShow = queue.slice(0, slidesToRender);
        const restQueue = queue.slice(slidesToRender);
        isInsertingSlides.current = true;
        setWinnersToShow(prev => [...prev, ...nextWinnersToShow]);
        setQueue(restQueue);
        // after inserting the new winners,the swiper should be slid
        // to the first slide without animation to avoid blinking
        swiper.slideTo(startIndex, 0, false);
        return;
      }

      const needToRemove =
        isInsertingSlides.current &&
        winnersToShow.length > slidesToRender &&
        swiper.realIndex % winnersToShow.length === endIndex;

      if (needToRemove) {
        const newWinnersToShow = winnersToShow.slice(winnersToShow.length - slidesToRender);
        isInsertingSlides.current = false;
        setWinnersToShow(newWinnersToShow);
        // after removing outdated winners, the swiper should be slid
        // to the first slide in the view
        // when the swiper is in the end position without animation to avoid blinking
        swiper.slideTo(slidesToRender - SLIDER_CONFIG.slidesPerView, 0, false);
      }
    },
    [winnersToShow, queue],
  );

  useEffect(() => {
    const { slidesToRender } = SLIDER_CONFIG;
    // if there are less slides than we need to render slider
    // then slides from the queue need to be added when they appear
    if (winnersToShow.length < slidesToRender && queue.length > 0) {
      const nextWinnersToShow = queue.slice(0, slidesToRender);
      const restQueue = queue.slice(slidesToRender);

      setWinnersToShow(prev => [...prev, ...nextWinnersToShow]);
      setQueue(restQueue);
    }
  }, [winnersToShow, queue]);

  useEffect(() => {
    const handler = ({ data }) => {
      setQueue(prev => cutQueue(prev, [data]));
    };

    Bus.on(Bus.events.casino.lastWinnerUpdate, handler);

    return () => Bus.off(Bus.events.casino.lastWinnerUpdate, handler);
  }, []);

  useEffect(() => {
    if (lastWinners.length === 0) return;

    const { slidesToRender } = SLIDER_CONFIG;

    setWinnersToShow(prev => [...prev, ...lastWinners.slice(0, slidesToRender)]);
    setQueue(prev => cutQueue(prev, lastWinners.slice(slidesToRender)));
  }, [lastWinners]);

  if (loading) {
    return (
      <div className="last-winner__skeleton">
        <LastWinnerSkeleton />
      </div>
    );
  }

  if (!isActive || !winnersToShow.length) {
    return null;
  }

  return (
    <div className="game-item-wrapper">
      <Slider
        autoplay={autoplayActive ? { delay: SLIDER_CONFIG.autoplayDelay } : false}
        direction="vertical"
        onBeforeInit={setSwiper}
        onSlideChangeTransitionEnd={onSlideChangeTransitionEnd}
        slideToClickedSlide={true}
        slidesPerView={SLIDER_CONFIG.slidesPerView}
      >
        {winnersToShow.map(winner => (
          <Slider.Slide key={winner.info.transactionId}>
            <LastWinnersItem
              autoplayStart={autoplayStart}
              autoplayStop={autoplayStop}
              isAuthenticated={isAuthenticated}
              status={status}
              winner={winner}
            />
          </Slider.Slide>
        ))}
      </Slider>
    </div>
  );
};

LastWinners.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  status: PropTypes.string,
};

export default LastWinners;
